import React, { useEffect, useMemo, useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { usePeriod } from "@/contexts/PeriodContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Table,
    TableHeader,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
} from "@/components/ui/table";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Trash2, Eraser, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

/**
 * Vorschau-Pflege:
 * Liste der "Vorschau-Titel" — 0-Lager, aber ET-Jahr im Fenster {Zeitraum-1, Zeitraum, Zeitraum+1}.
 * Mit Mehrfach-Auswahl + Bulk-Löschen (Titel + zugehörige Bewegungen werden komplett entfernt).
 */
export default function VorschauPflege() {
    const { activeTenant } = useTenant();
    const { zeitraum } = usePeriod();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        if (!activeTenant) return;
        setLoading(true);
        try {
            // Vorschau-Liste = Beispiel-Array aus Datenqualität-Endpoint
            const dq = await api.get("/dashboard/datenqualitaet", {
                params: { tenant_id: activeTenant.tenant_id, zeitraum },
            });
            const vorschau = dq.data.vorschau_null_alle || dq.data.vorschau_null_beispiele || [];
            // Pro ISBN die zugehörige Bewegung laden (für ET, HK-Werte etc.)
            // Wir verwenden den movements-Endpoint mit dem aktuellen Zeitraum
            const mov = await api.get("/movements", {
                params: { tenant_id: activeTenant.tenant_id, zeitraum, nur_aktive: true },
            });
            const vorschauIsbns = new Set(vorschau.map((v) => v.isbn));
            const list = (mov.data || [])
                .filter((m) => vorschauIsbns.has(m.isbn))
                .map((m) => ({
                    isbn: m.isbn,
                    haupttitel: m.haupttitel,
                    et: m.et,
                    et_jahr: m.et_jahr,
                    hk_ist: m.hk_ist,
                    hk_kal: m.hk_kal,
                    lager_oe: m.lager_oe || 0,
                    lager_de: m.lager_de || 0,
                }));
            // Sort by ET-Jahr desc, then ISBN
            list.sort((a, b) => {
                const ey = (b.et_jahr || 0) - (a.et_jahr || 0);
                if (ey !== 0) return ey;
                return (a.isbn || "").localeCompare(b.isbn || "");
            });
            setRows(list);
            setSelected(new Set());
        } catch {
            toast.error("Vorschau-Titel konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTenant?.tenant_id, zeitraum]);

    const filtered = useMemo(() => {
        if (!search.trim()) return rows;
        const q = search.toLowerCase().trim();
        return rows.filter(
            (r) =>
                (r.isbn || "").toLowerCase().includes(q) ||
                (r.haupttitel || "").toLowerCase().includes(q),
        );
    }, [rows, search]);

    const allFilteredSelected =
        filtered.length > 0 && filtered.every((r) => selected.has(r.isbn));

    const toggleAll = () => {
        const next = new Set(selected);
        if (allFilteredSelected) {
            filtered.forEach((r) => next.delete(r.isbn));
        } else {
            filtered.forEach((r) => next.add(r.isbn));
        }
        setSelected(next);
    };

    const toggleOne = (isbn) => {
        const next = new Set(selected);
        if (next.has(isbn)) next.delete(isbn);
        else next.add(isbn);
        setSelected(next);
    };

    const markeObvious = () => {
        // Auto-Auswahl: ohne HK Ist UND ohne HK Kal (klare Löschkandidaten)
        const next = new Set(selected);
        rows.forEach((r) => {
            const noHk = (!r.hk_ist || r.hk_ist === 0) && (!r.hk_kal || r.hk_kal === 0);
            if (noHk) next.add(r.isbn);
        });
        setSelected(next);
        toast.success(`${next.size} Titel ohne HK-Wert markiert`);
    };

    const formatEt = (iso) => {
        if (!iso) return "—";
        try {
            return new Date(iso).toLocaleDateString("de-DE", {
                day: "2-digit", month: "2-digit", year: "numeric",
            });
        } catch {
            return iso;
        }
    };

    const runDelete = async () => {
        setDeleting(true);
        try {
            const isbns = Array.from(selected);
            const r = await api.post("/titles/bulk-delete", {
                tenant_id: activeTenant.tenant_id,
                isbns,
            });
            toast.success(
                `${r.data.geloeschte_titel} Titel und ${r.data.geloeschte_bewegungen} Bewegungen gelöscht`,
            );
            setConfirmOpen(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-6 pb-12 animate-fade-up">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Daten</div>
                <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-bold mt-1">
                    Vorschau-Pflege
                </h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
                    Titel mit Bestand 0, aber ET-Jahr im Vorschau-Fenster (z.B. anstehende oder gerade erschienene
                    Bücher). Hier kannst Du Datenleichen schnell sammeln und löschen. <strong>Achtung:</strong>{" "}
                    Löschen entfernt sowohl die Stammdaten (Titel) als auch alle zugehörigen Bewegungen für{" "}
                    {activeTenant?.name}.
                </p>
            </div>

            <Card className="p-4 bg-surface1 border-border">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[220px]">
                        <Label className="text-xs">Suche (ISBN, Titel)</Label>
                        <div className="relative mt-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="z.B. Stinkesocke"
                                className="pl-9"
                                data-testid="vp-search-input"
                            />
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        onClick={markeObvious}
                        disabled={rows.length === 0}
                        data-testid="vp-mark-obvious-btn"
                    >
                        <Eraser className="h-4 w-4 mr-2" />
                        Klare Datenleichen markieren
                    </Button>
                    <Button
                        onClick={() => setConfirmOpen(true)}
                        disabled={selected.size === 0 || deleting}
                        className="bg-red-500/90 hover:bg-red-500 text-white"
                        data-testid="vp-delete-btn"
                    >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {selected.size > 0
                            ? `Auswahl löschen (${selected.size})`
                            : "Auswahl löschen"}
                    </Button>
                </div>
                <div className="mt-3 text-xs text-muted-foreground flex flex-wrap gap-4">
                    <span>Zeitraum {zeitraum}</span>
                    <span>{filtered.length} Vorschau-Titel</span>
                    {selected.size > 0 && (
                        <span className="text-red-400">{selected.size} markiert zum Löschen</span>
                    )}
                </div>
            </Card>

            <Card className="bg-surface1 border-border overflow-hidden">
                <div className="overflow-auto max-h-[68vh]">
                    <Table>
                        <TableHeader className="sticky top-0 bg-surface1 z-10">
                            <TableRow>
                                <TableHead className="w-[44px]">
                                    <Checkbox
                                        checked={allFilteredSelected}
                                        onCheckedChange={toggleAll}
                                        data-testid="vp-select-all"
                                        aria-label="Alle auswählen"
                                    />
                                </TableHead>
                                <TableHead className="w-[150px]">ISBN</TableHead>
                                <TableHead>Haupttitel</TableHead>
                                <TableHead className="w-[110px]">ET</TableHead>
                                <TableHead className="w-[80px] text-right">Lager Ö</TableHead>
                                <TableHead className="w-[80px] text-right">Lager D</TableHead>
                                <TableHead className="w-[100px] text-right">HK Kal</TableHead>
                                <TableHead className="w-[100px] text-right">HK Ist</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody data-testid="vp-table-body">
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                                        Lade …
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                                        Keine Vorschau-Titel für diesen Zeitraum.
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading &&
                                filtered.map((r) => {
                                    const isSel = selected.has(r.isbn);
                                    const noHk =
                                        (!r.hk_ist || r.hk_ist === 0) &&
                                        (!r.hk_kal || r.hk_kal === 0);
                                    return (
                                        <TableRow
                                            key={r.isbn}
                                            className={isSel ? "bg-red-500/5" : ""}
                                            data-testid={`vp-row-${r.isbn}`}
                                        >
                                            <TableCell>
                                                <Checkbox
                                                    checked={isSel}
                                                    onCheckedChange={() => toggleOne(r.isbn)}
                                                    aria-label={`Auswahl ${r.isbn}`}
                                                    data-testid={`vp-check-${r.isbn}`}
                                                />
                                            </TableCell>
                                            <TableCell className="font-mono text-xs">{r.isbn}</TableCell>
                                            <TableCell className="max-w-[300px] truncate" title={r.haupttitel}>
                                                {r.haupttitel}
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground tabular whitespace-nowrap">
                                                {formatEt(r.et)}
                                            </TableCell>
                                            <TableCell className="text-right tabular text-xs">
                                                {r.lager_oe}
                                            </TableCell>
                                            <TableCell className="text-right tabular text-xs">
                                                {r.lager_de}
                                            </TableCell>
                                            <TableCell
                                                className={`text-right tabular text-xs ${
                                                    !r.hk_kal ? "text-muted-foreground italic" : ""
                                                }`}
                                            >
                                                {r.hk_kal ? r.hk_kal.toFixed(4) : "—"}
                                            </TableCell>
                                            <TableCell
                                                className={`text-right tabular text-xs ${
                                                    !r.hk_ist ? "text-muted-foreground italic" : ""
                                                }`}
                                            >
                                                {r.hk_ist ? r.hk_ist.toFixed(4) : "—"}
                                                {noHk && (
                                                    <span
                                                        className="ml-1 text-[9px] uppercase tracking-widest text-red-400/80"
                                                        title="Keine HK-Werte — klare Datenleiche"
                                                    >
                                                        leer
                                                    </span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <ShieldAlert className="h-5 w-5 text-red-400" />
                            {selected.size} Titel endgültig löschen?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-2 text-sm">
                                <p>
                                    Folgendes wird gelöscht für <strong>{activeTenant?.name}</strong>:
                                </p>
                                <ul className="list-disc pl-5 text-muted-foreground">
                                    <li>{selected.size} Stammdatensätze (Titel)</li>
                                    <li>Alle dazugehörigen Bewegungen über alle Zeiträume</li>
                                </ul>
                                <p className="text-red-400 font-medium mt-2">
                                    Diese Aktion ist nicht rückgängig zu machen.
                                </p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={runDelete}
                            disabled={deleting}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="vp-confirm-delete"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {deleting ? "Lösche …" : "Ja, endgültig löschen"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
